import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useLanguage } from '../../i18n';
import styles from './SupportDirectory.module.css';

// Dummy data – in real app from API/state
const dummyOrganizations = [
  {
    id: 1,
    name: "Global Rights Foundation",
    type: "Digital Security Helpdesk",
    description: "24/7 emergency digital security support, incident response, secure comms guidance for activists.",
    tags: ["24/7", "Global", "Multilingual", "Incident Response"],
    website: "globalrights.foundation",
    phone: "+1-800-SECURE (732-873)",
    region: "Global",
    link: "/support/global-rights"
  },
  {
    id: 2,
    name: "Front Line Defenders",
    type: "Emergency Protection & Grants",
    description: "Rapid response grants, emergency relocation, protection advice for human rights defenders facing threats.",
    tags: ["Emergency Grants", "Relocation", "24/7", "Physical Safety"],
    website: "frontlinedefenders.org",
    phone: "+353-1-210-0489",
    region: "Global",
    link: "/support/front-line-defenders"
  },
  {
    id: 3,
    name: "Access Now Helpline",
    type: "Digital Rights Emergency Response",
    description: "Immediate assistance for digital threats, account takeovers, spyware infections, and censorship circumvention.",
    tags: ["Digital Threats", "Spyware", "Censorship", "24/7"],
    website: "accessnow.org/help",
    phone: "+1-202-803-0844",
    region: "Global",
    link: "/support/access-now"
  },
  {
    id: 4,
    name: "DefendDefenders",
    type: "East & Horn of Africa Protection",
    description: "Regional support for human rights defenders including training, emergency grants, and advocacy.",
    tags: ["Africa", "Training", "Grants", "Advocacy"],
    website: "defenddefenders.org",
    phone: "+256-414-510-263",
    region: "East & Horn of Africa",
    link: "/support/defenddefenders"
  }
];

export default function SupportDirectory() {
  const { t } = useLanguage();

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        {/* Emergency Banner – always at top */}
        <section className={styles.emergencyBanner}>
          <div className={styles.bannerInner}>
            <Icon icon="lucide:phone" className={styles.bannerIcon} />
            <div className={styles.bannerTextBlock}>
              <h2 className={styles.bannerTitle}>{t('inImmediateDanger')}</h2>
              <p className={styles.bannerSubtitle}>
                {t('bannerSubtitle')}
              </p>
            </div>
            <Link to="/support" className={styles.bannerButton}>
              {t('getHelpNow')}
              <Icon icon="lucide:arrow-right" />
            </Link>
          </div>
        </section>

        {/* Page Header */}
        <header className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>{t('supportDirectory')}</h1>
          <p className={styles.pageSubtitle}>
            {t('supportDirectorySubtitle')}
          </p>

          <div className={styles.controls}>
            <div className={styles.searchWrapper}>
              <input
                type="text"
                placeholder={t('searchOrganizationsPlaceholder')}
                className={styles.searchInput}
              />
              <Icon icon="lucide:search" className={styles.searchIcon} />
            </div>

            <div className={styles.filterRow}>
              <select className={styles.filterSelect}>
                <option value="all">{t('allRegions')}</option>
                <option value="global">{t('global')}</option>
                <option value="africa">{t('africa')}</option>
                <option value="asia-pacific">{t('asiaPacific')}</option>
                <option value="latin-america">{t('latinAmerica')}</option>
                <option value="europe">{t('europe')}</option>
                <option value="mena">{t('mena')}</option>
              </select>

              <select className={styles.filterSelect}>
                <option value="all">{t('allServices')}</option>
                <option value="digital">{t('digitalSecurityService')}</option>
                <option value="legal">{t('legalAid')}</option>
                <option value="relocation">{t('emergencyRelocation')}</option>
                <option value="mental">{t('mentalHealthService')}</option>
                <option value="physical">{t('physicalSafety')}</option>
              </select>

              <button className={styles.filterButton}>
                <Icon icon="lucide:filter" />
                {t('moreFilters')}
              </button>
            </div>
          </div>
        </header>

        {/* Directory Grid */}
        <section className={styles.directoryGrid}>
          {dummyOrganizations.map((org) => (
            <Link to={org.link} key={org.id} className={styles.orgCard}>
              <div className={styles.cardHeader}>
                <div className={styles.orgLogoPlaceholder}></div>
                <div className={styles.orgInfo}>
                  <h3 className={styles.orgName}>{org.name}</h3>
                  <span className={styles.orgType}>{org.type}</span>
                </div>
              </div>

              <div className={styles.cardBody}>
                <p className={styles.orgDescription}>{org.description}</p>

                <div className={styles.orgTags}>
                  {org.tags.map((tag, idx) => (
                    <span key={idx} className={styles.tag}>{tag}</span>
                  ))}
                </div>

                <div className={styles.orgContact}>
                  <div className={styles.contactItem}>
                    <Icon icon="lucide:globe" />
                    <span>{org.website}</span>
                  </div>
                  <div className={styles.contactItem}>
                    <Icon icon="lucide:phone" />
                    <span>{org.phone}</span>
                  </div>
                  <div className={styles.contactItem}>
                    <Icon icon="lucide:map-pin" />
                    <span>{org.region}</span>
                  </div>
                </div>
              </div>

              <div className={styles.cardFooter}>
                <Link to={`/support/${org.link.split('/').pop()}`} className={styles.btnPrimary}>
                   {t('contactSupport')}
                </Link>
              </div>
            </Link>
          ))}
        </section>

        {/* Load More */}
        {/* <div className={styles.loadMoreSection}>
          <button className={styles.loadMoreBtn}>
            {t('loadMoreOrganizations')}
            <Icon icon="lucide:arrow-down" />
          </button>
          <p className={styles.paginationInfo}>
            {t('showingProviders', { count: dummyOrganizations.length, total: 84 })}
          </p>
        </div> */}
      </div>
    </main>
  );
}

//         </header>

//         {/* Directory Grid */}
//         <section className={styles.directoryGrid}>
//           {/* Example support organization card */}
//           <article className={styles.supportCard}>
//             <div className={styles.cardHeader}>
//               <div className={styles.orgLogoPlaceholder}></div>
//               <div>
//                 <h3 className={styles.orgName}>Global Rights Foundation</h3>
//                 <span className={styles.orgType}>Digital Security Helpdesk</span>
//               </div>
//             </div>

//             <div className={styles.cardBody}>
//               <p className={styles.orgDescription}>
//                 24/7 emergency digital security support, incident response, and secure communication guidance for activists and organizations.
//               </p>

//               <div className={styles.orgTags}>
//                 <span className={styles.tag}>24/7 Hotline</span>
//                 <span className={styles.tag}>Global</span>
//                 <span className={styles.tag}>Multilingual</span>
//               </div>

//               <div className={styles.orgMeta}>
//                 <div className={styles.metaItem}>
//                   <Icon icon="lucide:globe" />
//                   <span>globalrights.foundation</span>
//                 </div>
//                 <div className={styles.metaItem}>
//                   <Icon icon="lucide:phone" />
//                   <span>+1-800-SECURE (732-873)</span>
//                 </div>
//               </div>
//             </div>

//             <div className={styles.cardFooter}>
//               <button className={styles.btnPrimary}>
//                 Contact Support
//               </button>
//               <button className={styles.btnOutlineSmall}>
//                 View Profile
//               </button>
//             </div>
//           </article>

//           {/* Second example card */}
//           <article className={styles.supportCard}>
//             <div className={styles.cardHeader}>
//               <div className={styles.orgLogoPlaceholder}></div>
//               <div>
//                 <h3 className={styles.orgName}>Front Line Defenders</h3>
//                 <span className={styles.orgType}>Emergency Grants & Protection</span>
//               </div>
//             </div>

//             <div className={styles.cardBody}>
//               <p className={styles.orgDescription}>
//                 Rapid response grants, emergency relocation support, and protection advice for human rights defenders facing threats.
//               </p>

//               <div className={styles.orgTags}>
//                 <span className={styles.tag}>Emergency Grants</span>
//                 <span className={styles.tag}>Relocation</span>
//                 <span className={styles.tag}>24/7</span>
//               </div>

//               <div className={styles.orgMeta}>
//                 <div className={styles.metaItem}>
//                   <Icon icon="lucide:globe" />
//                   <span>frontlinedefenders.org</span>
//                 </div>
//                 <div className={styles.metaItem}>
//                   <Icon icon="lucide:phone" />
//                   <span>+353-1-210-0489</span>
//                 </div>
//               </div>
//             </div>

//             <div className={styles.cardFooter}>
//               <button className={styles.btnPrimary}>
//                 Get Emergency Support
//               </button>
//               <button className={styles.btnOutlineSmall}>
//                 Learn More
//               </button>
//             </div>
//           </article>

//           {/* You would map more cards here in a real app */}
//         </section>

//         {/* Load more / Pagination */}
//         <div className={styles.pagination}>
//           <button className={styles.loadMoreBtn}>
//             Load More Organizations
//             <Icon icon="lucide:arrow-down" />
//           </button>
//           <p className={styles.paginationInfo}>
//             Showing 1–12 of 84 verified support providers
//           </p>
//         </div>
//       </div>
//     </main>
//   );
// }